/**
 * Extracts world-space (Z-up) surface geometry for every illuminated layer from
 * the LIVE R3F scene, for the client-side measurement calculation.
 *
 * Must be used inside the workspace <Canvas> (relies on useThree). The live
 * scene already carries the Z-up transform (unlike the path tracer's
 * sceneExtract, which re-loads files), so baking `mesh.matrixWorld` yields the
 * same coordinate space ml uses. Keyed by the layer's threeJsName
 * (= mesh.userData.layerName). See docs/frontend-measurement-calculation.md.
 */
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

import type { RootState } from '../../../store/store';
import type { SurfaceGeometry } from '../../../utils/measurementCalculation/types';

export const useIlluminatedGeometry = (): (() => Map<string, SurfaceGeometry>) => {
  const { scene } = useThree();
  const modelLayers = useSelector((s: RootState) => s.workspace.modelLayers);

  return useCallback(() => {
    // ALL layer meshes (not only illuminated) — taman-ml computes measurement
    // points for every mesh in the scene, including non-target ones the lamps see.
    const layerNames = new Set(modelLayers.map((l) => l.threeJsName));
    const result = new Map<string, SurfaceGeometry>();
    if (!layerNames.size) return result;

    scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const layerName = obj.userData.layerName as string | undefined;
      if (!layerName || !layerNames.has(layerName)) return;

      const geometry = (obj.geometry as THREE.BufferGeometry).clone();
      obj.updateWorldMatrix(true, false);
      geometry.applyMatrix4(obj.matrixWorld); // bake to world space (scene is already Z-up)
      if (!geometry.attributes.normal) geometry.computeVertexNormals();

      const positions = (geometry.attributes.position as THREE.BufferAttribute).array;
      const index = geometry.index ? geometry.index.array : undefined;
      result.set(layerName, { positions, index });
    });

    return result;
  }, [scene, modelLayers]);
};
